import { useSettingsApiClientWrapper, useAuth, useSettingsMarketplace, useMasterData } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { PermissionLabel } from '@spike/masterdata-model';
import { MarketplaceStatus, saveMarketplacePermissionsThunk } from '@spike/marketplace-action';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './Permissions.module.scss';

type PermissionSwitches = Map<string, unknown>;
type PermissionRole = {
    key: string;
    label: string;
};

const getMapEntries = (value: unknown): Array<[string, unknown]> => {
    if (value instanceof Map) {
        return Array.from(value.entries()).map(([key, item]) => [String(key), item]);
    }

    if (!value || typeof value !== 'object') {
        return [];
    }

    return Object.entries(value as Record<string, unknown>);
};

const toBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
        return value > 0;
    }

    return false;
};

const getRolePermissions = (permissionName: string): string => {
    return permissionName.split('.')[0] || permissionName;
};

const isManagePermissionKey = (permissionKey: string): boolean => permissionKey.endsWith('.manage');

const getPermissionPrefix = (permissionKey: string): string => {
    const [prefix] = permissionKey.split('.');
    return prefix ? `${prefix}.` : '';
};

const getManagePermissionKey = (permissionKey: string): string => {
    const permissionPrefix = getPermissionPrefix(permissionKey);
    return permissionPrefix ? `${permissionPrefix}manage` : 'manage';
};

const toRoleString = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return undefined;
};

const isOwnerRoleKey = (roleKey: string): boolean => roleKey.trim().toLowerCase() === 'owner';

const getMasterdataPermissionRoles = (masterdata: unknown): Array<PermissionRole> => {
    if (!masterdata || typeof masterdata !== 'object') {
        return [];
    }

    const md = masterdata as Record<string, unknown>;
    const rolesCandidate = md.permissionRoles ?? md.permissionsRoles ?? md.roles;

    if (!Array.isArray(rolesCandidate)) {
        return [];
    }

    return rolesCandidate
        .map(role => {
            if (!role || typeof role !== 'object') {
                return undefined;
            }

            const roleRecord = role as Record<string, unknown>;
            const key =
                toRoleString(roleRecord.key) ??
                toRoleString(roleRecord.value) ??
                toRoleString(roleRecord.code) ??
                toRoleString(roleRecord.id);

            const label =
                toRoleString(roleRecord.label) ??
                toRoleString(roleRecord.name) ??
                toRoleString(roleRecord.title) ??
                key;

            if (!key || !label) {
                return undefined;
            }

            return { key, label };
        })
        .filter((role): role is PermissionRole => role !== undefined);
};

const isDefined = <T,>(value: T | undefined): value is T => value !== undefined;

const updateRolePermissionEntries = (rolePermissions: unknown, permissionKey: string, enabled: boolean): unknown => {
    const shouldCascadeGroupChange = isManagePermissionKey(permissionKey);
    const permissionPrefix = shouldCascadeGroupChange ? getPermissionPrefix(permissionKey) : '';
    const shouldEnableManagePermission = enabled && !isManagePermissionKey(permissionKey);
    const managePermissionKey = shouldEnableManagePermission ? getManagePermissionKey(permissionKey) : '';

    if (rolePermissions instanceof Map) {
        const nextRolePermissions = new Map(rolePermissions);

        if (shouldCascadeGroupChange && permissionPrefix) {
            for (const rolePermissionKey of Array.from(nextRolePermissions.keys())) {
                if (String(rolePermissionKey).startsWith(permissionPrefix)) {
                    nextRolePermissions.set(rolePermissionKey, enabled);
                }
            }
        } else {
            nextRolePermissions.set(permissionKey, enabled);

            if (shouldEnableManagePermission && managePermissionKey) {
                nextRolePermissions.set(managePermissionKey, true);
            }
        }

        return nextRolePermissions;
    }

    if (rolePermissions && typeof rolePermissions === 'object') {
        const nextRolePermissions = {
            ...(rolePermissions as Record<string, unknown>)
        };

        if (shouldCascadeGroupChange && permissionPrefix) {
            for (const rolePermissionKey of Object.keys(nextRolePermissions)) {
                if (rolePermissionKey.startsWith(permissionPrefix)) {
                    nextRolePermissions[rolePermissionKey] = enabled;
                }
            }
        } else {
            nextRolePermissions[permissionKey] = enabled;

            if (shouldEnableManagePermission && managePermissionKey) {
                nextRolePermissions[managePermissionKey] = true;
            }
        }

        return nextRolePermissions;
    }

    return rolePermissions;
};

const updateRolePermissions = (
    permissions: unknown,
    groupKey: string,
    permissionKey: string,
    enabled: boolean
): unknown => {
    if (permissions instanceof Map) {
        const nextPermissions = new Map(permissions);
        const rolePermissions = nextPermissions.get(groupKey);
        nextPermissions.set(groupKey, updateRolePermissionEntries(rolePermissions, permissionKey, enabled));
        return nextPermissions;
    }

    if (permissions && typeof permissions === 'object') {
        const permissionsRecord = permissions as Record<string, unknown>;
        return {
            ...permissionsRecord,
            [groupKey]: updateRolePermissionEntries(permissionsRecord[groupKey], permissionKey, enabled)
        };
    }

    return permissions;
};

const orderPermissionEntriesByLabels = (
    entries: Array<[string, unknown]>,
    permissionLabels: Array<PermissionLabel>
): Array<[string, unknown]> => {
    const entryMap = new Map(entries);
    const ordered: Array<[string, unknown]> = [];
    const used = new Set<string>();

    for (const { key } of permissionLabels) {
        if (!used.has(key) && entryMap.has(key)) {
            ordered.push([key, entryMap.get(key)!]);
            used.add(key);
        }
    }

    const remainder = entries
        .filter(([key]) => !used.has(key))
        .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey));
    ordered.push(...remainder);

    return ordered;
};

interface Props {
    openSidebar?: () => void;
    isMobile?: boolean;
}

const Permissions: FunctionComponent<Props> = () => {
    const auth = useAuth();
    const marketplace = useSettingsMarketplace();
    const masterdata = useMasterData();
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const [selectedRolePermissions, setSelectedRolePermissions] = useState<string>();
    const [permissionsState, setPermissionsState] = useState<unknown>(marketplace.permissions);
    const [isSavingPermissions, setIsSavingPermissions] = useState<boolean>(false);
    const [savingPermissionKey, setSavingPermissionKey] = useState<string>();

    useEffect(() => {
        setPermissionsState(marketplace.permissions);
    }, [marketplace.permissions]);

    useEffect(() => {
        switch (marketplaceStatus) {
            case MarketplaceStatus.SavingPermissions:
                setIsSavingPermissions(true);
                break;
            case MarketplaceStatus.SavePermissionsSuccess:
            case MarketplaceStatus.Error:
                setIsSavingPermissions(false);
                setSavingPermissionKey(undefined);
                break;
            default:
                break;
        }
    }, [marketplaceStatus]);

    const permissionLabelByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const { key, label } of masterdata.permissionLabels) {
            map.set(key, label);
        }
        return map;
    }, [masterdata.permissionLabels]);

    const permissionEntries = useMemo(() => {
        return getMapEntries(permissionsState);
    }, [permissionsState]);

    const loggedUserRoleKey = useMemo(() => {
        const roleKey = auth?.user?.role?.key;
        return typeof roleKey === 'string' ? roleKey.trim().toLowerCase() : '';
    }, [auth?.user?.role?.key]);

    const permissionOptions: Array<SelectOption<PermissionSwitches>> = useMemo(() => {
        const permissionMap = new Map(permissionEntries);
        const roleOptions = getMasterdataPermissionRoles(masterdata)
            .map<SelectOption<PermissionSwitches> | undefined>(role => {
                const rolePermissions = permissionMap.get(role.key);
                if (rolePermissions === undefined) {
                    return undefined;
                }

                return {
                    label: role.label,
                    value: role.key,
                    data: rolePermissions as PermissionSwitches
                };
            })
            .filter(isDefined)
            .filter(option => !isOwnerRoleKey(option.value))
            .filter(option => option.value.trim().toLowerCase() !== loggedUserRoleKey);

        if (roleOptions.length > 0) {
            return roleOptions;
        }

        return permissionEntries
            .filter(([permissionKey]) => !isOwnerRoleKey(permissionKey))
            .filter(([permissionKey]) => permissionKey.trim().toLowerCase() !== loggedUserRoleKey)
            .map(([permissionKey, value]) => ({
                label: permissionKey,
                value: permissionKey,
                data: value as PermissionSwitches
            }));
    }, [permissionEntries, masterdata, loggedUserRoleKey]);

    const currentRolePermissions = useMemo(() => {
        if (selectedRolePermissions && permissionOptions.some(option => option.value === selectedRolePermissions)) {
            return selectedRolePermissions;
        }

        return permissionOptions[0]?.value;
    }, [permissionOptions, selectedRolePermissions]);

    const handlePermissionChange = (permissionKey: string, enabled: boolean): void => {
        if (!currentRolePermissions || isSavingPermissions) {
            return;
        }

        const nextPermissions = updateRolePermissions(permissionsState, currentRolePermissions, permissionKey, enabled);

        setPermissionsState(nextPermissions);
        setIsSavingPermissions(true);
        setSavingPermissionKey(permissionKey);

        dispatch(
            saveMarketplacePermissionsThunk(apiClientWrapper, nextPermissions as Map<string, Map<string, boolean>>)
        );
    };

    const switches = useMemo(() => {
        if (!currentRolePermissions) {
            return [];
        }

        const selectedOption = permissionOptions.find(option => option.value === currentRolePermissions);

        const sortedPermissions = orderPermissionEntriesByLabels(
            getMapEntries(selectedOption?.data),
            masterdata.permissionLabels
        ).map(([permissionName, permissionEnabled]) => ({
            name: permissionName,
            enabled: toBoolean(permissionEnabled)
        }));

        return sortedPermissions.map((permission, index) => {
            if (index === 0) {
                return {
                    ...permission,
                    showGroupDivider: false
                };
            }

            const previousPermission = sortedPermissions[index - 1];
            const groupChanged = getRolePermissions(previousPermission.name) !== getRolePermissions(permission.name);

            return {
                ...permission,
                showGroupDivider: groupChanged
            };
        });
    }, [currentRolePermissions, permissionOptions, masterdata.permissionLabels]);

    return (
        <div className={styles.container}>
            <p className={styles.title}>Permissions</p>
            <p className={styles.subtitle}>Select the areas accessible to each permission role.</p>

            <div className={styles.content}>
                <div className={styles.leftColumn}>
                    <p className={styles.sectionLabel}>Select Role</p>
                    <Select<PermissionSwitches>
                        options={permissionOptions}
                        placeholder="Select a role"
                        value={currentRolePermissions}
                        disabled={isSavingPermissions}
                        onChange={value => setSelectedRolePermissions(value)}
                    />
                </div>

                <div className={styles.rightColumn}>
                    <p className={styles.sectionLabel}>Set Permissions</p>

                    <div className={styles.permissionList}>
                        {switches.map(permission => (
                            <div
                                key={permission.name}
                                className={
                                    permission.showGroupDivider
                                        ? `${styles.permissionRow} ${styles.permissionRowGroupDivider}`
                                        : styles.permissionRow
                                }
                            >
                                <p
                                    className={
                                        isManagePermissionKey(permission.name)
                                            ? styles.permissionTextManage
                                            : `${styles.permissionText} ${styles.permissionTextIndented}`
                                    }
                                >
                                    {permissionLabelByKey.get(permission.name) ?? permission.name}
                                </p>
                                {isSavingPermissions && savingPermissionKey === permission.name ? (
                                    <div className={styles.switchSpinnerWrapper}>
                                        <Spinner size={20} />
                                    </div>
                                ) : (
                                    <Switch
                                        className={styles.switchControl}
                                        checked={permission.enabled}
                                        disabled={isSavingPermissions}
                                        onChange={checked => handlePermissionChange(permission.name, checked)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Permissions;
